/**
 * Reemplaza a lib/supabase.ts. En vez de @supabase/supabase-js, un cliente
 * fetch() delgado contra el Worker (src/worker.ts en rat-cloudflare-spike).
 * En dev, vite.config.ts hace de proxy hacia http://127.0.0.1:8787, asi que
 * no hace falta CORS ni una URL absoluta.
 */
import { friendlyMessage, type GateInfo } from './errors';

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '';
const TOKEN_KEY = 'rat_cf_token';

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

/**
 * `message` es el texto AMIGABLE (lib/errors.ts): toda pantalla que muestre `error.message` queda traducida sin
 * cambios. El codigo crudo del backend sigue en `code` (para decidir logica) y, si la compuerta de estado de una
 * unidad bloqueo el cambio, el detalle en `gate`.
 */
export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    public gate?: GateInfo,
  ) {
    super(friendlyMessage(code, gate, status));
  }
}

/** Arma el ApiError a partir del cuerpo de una respuesta de error (solo el codigo HTTP importa si el cuerpo no es JSON). */
function apiErrorFrom(status: number, body: unknown): ApiError {
  const b = (body ?? {}) as { error?: string; gate?: GateInfo };
  return new ApiError(status, b.error ?? 'unknown_error', b.gate);
}

/**
 * `tokenOverride` -- para las rutas /mfa/* durante el enrolamiento/desafio:
 * ahi la sesion real todavia no existe, solo un "pending token" de 5
 * minutos (ver src/auth.ts en el backend) que NUNCA debe pisar el token de
 * sesion guardado en localStorage. Sin override, se usa el token normal.
 */
export async function apiFetch<T>(path: string, init: RequestInit = {}, tokenOverride?: string): Promise<T> {
  const token = tokenOverride ?? getToken();
  const headers = new Headers(init.headers);
  headers.set('content-type', 'application/json');
  if (token) headers.set('Authorization', `Bearer ${token}`);

  const res = await fetch(BASE_URL + path, { ...init, headers });
  if (!res.ok) {
    // Solo aqui se tolera un cuerpo no-JSON (o vacio): lo unico que importa
    // de una respuesta de error es el codigo HTTP, `error` es un extra.
    const body = await res.json().catch(() => ({}));
    throw apiErrorFrom(res.status, body);
  }
  // Una respuesta 200 que no es JSON real es un bug (ruta no proxied -> cae
  // al index.html de la SPA, por ejemplo) -- debe reventar aqui, no volverse
  // silenciosamente un {} que rompe mas abajo con un error confuso.
  return (await res.json()) as T;
}

/**
 * Tres formas de respuesta segun `auth.require_mfa` (app_settings) y si el
 * usuario ya esta enrolado -- ver `login()` en src/auth.ts del backend.
 * Con el flag apagado (default de hoy) siempre es 'ok', identico a antes.
 */
export type LoginOutcome =
  | { status: 'ok' }
  | { status: 'mfa_enroll_required'; pendingToken: string }
  | { status: 'mfa_challenge_required'; pendingToken: string };

export async function login(email: string, password: string): Promise<LoginOutcome> {
  const body = await apiFetch<{ token?: string; mfa_required?: boolean; phase?: 'enroll' | 'challenge'; pending_token?: string }>(
    '/login',
    { method: 'POST', body: JSON.stringify({ email, password }) },
  );
  if (body.token) {
    setToken(body.token);
    return { status: 'ok' };
  }
  return {
    status: body.phase === 'enroll' ? 'mfa_enroll_required' : 'mfa_challenge_required',
    pendingToken: body.pending_token!,
  };
}

export interface MfaEnrollStart {
  secret: string;
  otpauthUrl: string;
}

/** Genera (o regenera) el secreto TOTP -- mfa_enabled sigue en 0 hasta confirmar con un codigo real. */
export async function mfaEnrollStart(pendingToken: string): Promise<MfaEnrollStart> {
  return apiFetch<MfaEnrollStart>('/mfa/enroll', { method: 'POST' }, pendingToken);
}

/** Confirma el enrolamiento con un codigo de 6 digitos -- recien aqui entrega una sesion real. */
export async function mfaEnrollConfirm(pendingToken: string, code: string): Promise<string> {
  const { token } = await apiFetch<{ ok: true; token: string }>(
    '/mfa/enroll/confirm',
    { method: 'POST', body: JSON.stringify({ code }) },
    pendingToken,
  );
  return token;
}

/** Desafio de login para un usuario YA enrolado. */
export async function mfaChallenge(pendingToken: string, code: string): Promise<string> {
  const { token } = await apiFetch<{ ok: true; token: string }>(
    '/mfa/challenge',
    { method: 'POST', body: JSON.stringify({ code }) },
    pendingToken,
  );
  return token;
}

export function logout(): void {
  clearToken();
}

/**
 * Subida de archivos (adjuntos) -- NO puede pasar por apiFetch() porque este
 * fuerza `content-type: application/json`, lo que rompe el boundary de
 * multipart/form-data (el browser lo agrega solo al fijar `body` a un
 * FormData, siempre que el header no este seteado a mano).
 */
export async function apiUpload<T>(path: string, form: FormData): Promise<T> {
  const token = getToken();
  const headers = new Headers();
  if (token) headers.set('Authorization', `Bearer ${token}`);
  const res = await fetch(BASE_URL + path, { method: 'POST', headers, body: form });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw apiErrorFrom(res.status, body);
  }
  return (await res.json()) as T;
}

/** Descarga de archivos -- la respuesta es el binario, no JSON, asi que tampoco pasa por apiFetch(). */
export async function apiDownload(path: string): Promise<{ blob: Blob; filename: string | null }> {
  const token = getToken();
  const headers = new Headers();
  if (token) headers.set('Authorization', `Bearer ${token}`);
  const res = await fetch(BASE_URL + path, { headers });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw apiErrorFrom(res.status, body);
  }
  const match = res.headers.get('content-disposition')?.match(/filename="([^"]*)"/);
  return { blob: await res.blob(), filename: match?.[1] ?? null };
}
